import checkTypes from './checkTypes.js';

// Price formatting exceptions (Format that can't be properly extracted from provided formatted price)
const priceFormattingExceptions = [{
	currencySymbol: 'Rp', // en-id, Indonesian Rupiah
	thousandSeparator: '.',
	decimalSeparator: ','
}];

/**
 * Parse the given price, extract the price format
 * @param {string} formattedPrice
 * @returns {object}
 */
const parsePrice = function(formattedPrice) {
	// Extract the prefix and suffix from the price
	const prefix = /^[\D\s]*/.exec(formattedPrice)[0];
	const suffix = /[\D\s]*$/.exec(formattedPrice)[0];
	// Clean the value for prefix and suffix
	const price = formattedPrice.replace(prefix, '').replace(suffix, '');
	// Get the currency symbol whether it's before or after the value
	const currencySymbol = prefix.length ? prefix : suffix;
	// Get thousands separator, fallback to no separator
	const foundThousandSeparator = /([,.])([0-9]{3})/.exec(price);
	let thousandSeparator = foundThousandSeparator && foundThousandSeparator[1] ? foundThousandSeparator[1] : '';
	// Get decimals separator, fallback if no decimal separator found
	const foundDecimalSeparator = /([,.])[0-9]{2}$/.exec(price);
	let decimalSeparator = thousandSeparator.trim() === '' ? ',' : '.';
	if (foundDecimalSeparator && foundDecimalSeparator[1]) {
		decimalSeparator = foundDecimalSeparator[1];
	}
	// Force separators for currency exceptions
	priceFormattingExceptions.forEach((exception) => {
		if (exception.currencySymbol === currencySymbol) {
			thousandSeparator = exception.thousandSeparator;
			decimalSeparator = exception.decimalSeparator;
		}
	});
	// Check if currency is before or after the value and create the price format placeholders
	const priceFormat = `${prefix.length ? '#c' : ''}#p${suffix.length ? '#c' : ''}`;
	// Create formatted and unformatted variant of the price
	const priceSplit = price.split(/([,.])([0-9]{2})$/);
	const number = priceSplit[0].replace(/\D+/g, '');
	const decimals = priceSplit[1] ? `.${priceSplit[2]}` : '';
	const unformattedPrice = Number(`${number}${decimals}`);
	return {
		formatted: formattedPrice,
		unformatted: unformattedPrice,
		format: {
			currencySymbol,
			decimalSeparator,
			thousandSeparator,
			priceFormat
		}
	};
};

/**
 * Add thousand separator to the price
 * @param {string} price
 * @param {string} thousandSeparator
 * @returns {string}
 */
const separateThousands = function(price, thousandSeparator) {
	const chunks = [];
	for (let i = price.length; i > 0; i -= 3) {
		chunks.push(price.substring(i, i - 3));
	}
	return chunks.reverse().join(thousandSeparator);
};

/**
 * @class Price
 */
export default class Price {

	/**
	 * Init the Price class
	 * @param {string} formattedPrice
	 * @public
	 * @memberof Price
	 */
	constructor(formattedPrice) {
		checkTypes('Price.constructor', {
			price: [formattedPrice, 'string']
		});
		this.price = parsePrice(formattedPrice);
	}

	/**
	 * Format the number according the passed price format
	 * @param {number} unformattedPrice
	 * @returns {object}
	 * @public
	 * @memberof Price
	 */
	formatPrice(unformattedPrice) {
		const fixedValue = unformattedPrice.toFixed(2);
		const [price, decimals] = fixedValue.split('.');
		const priceWithSeparators = separateThousands(price, this.price.format.thousandSeparator);
		const decimalsWithSeparator = `${this.price.format.decimalSeparator}${decimals}`;
		const formatted = `${priceWithSeparators}${decimalsWithSeparator}`;
		return {
			formatted: this.price.format.priceFormat.replace('#c', this.price.format.currencySymbol).replace('#p', formatted),
			unformatted: parseFloat(fixedValue)
		};
	}

	/**
	 * Get monthly price representation
	 * @param {number} [months=12]
	 * @returns {object}
	 * @public
	 * @memberof Price
	 */
	getMonthly(months = 12) {
		checkTypes('Price.getMonthlyPrice', {
			period: [months, 'number']
		});
		return this.formatPrice(this.price.unformatted / months);
	}

	/**
	 * Sum product discount/tax/list/street
	 * @param {object} products
	 * @param {string} type - discount/tax/list/street
	 * @returns {object}
	 */
	sumPrices(products, type) {
		let sum = 0;
		products.forEach((product) => {
			if (type === 'discount' && product.price.discount) {
				sum += product.price.discount.absolute.unformatted;
			} else if (product.price[type]) {
				sum += product.price[type].unformatted;
			}
		});
		return this.formatPrice(sum);
	}

	/**
	 * Calculate sutotal
	 * @param {number} listPrice
	 * @param {number} tax
	 * @returns {object}
	 */
	calcSubtotal(listPrice, tax) {
		return this.formatPrice(listPrice - tax);
	}

}
